import React, { useMemo, useState } from "react"
import { DateTime } from "luxon"
import { Box, Grid, Stack } from "@mui/material"
import { UseQueryResult } from "@tanstack/react-query"
import { MRT_ColumnDef, MRT_FullScreenToggleButton } from "material-react-table"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
// import { SelectAdyenAccountAutocomplete } from "../components/SelectAdyenAccountAutocomplete"
import { SelectMerchantAutocomplete } from "../components/SelectMerchantAutocomplete"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { SUPPLIER_ADMIN_ROUTE } from "../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    fetchSupplierAcquiringFees,
    SupplierAcquiringReportsQuery,
    SupplierAcquiringFeesResponse,
} from "../supplierAcquiringReportsApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"

import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../shared/components/table/TableSummaryBox"
import { DateType, FeeItem } from "../supplierAcquiringReportsTypes"
import { useSupplier } from "../../../supplierHooks"
import { filterDataByMerchantAndAdyentId } from "../reconciliationLib"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { useExportAcquiringReportsFeesToCsv } from "../hooks/useExportAcquiringReportsFeesToCsv"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { InfoBox } from "../../../../merchant/admin/shops/components/formElements/InfoBox"
import { StandardToggleGlobalFilterButton } from "../../../../../shared/components/table/StandardToggleGlobalFilterButton"
import { makeStyles } from "tss-react/mui"
import { SelectDateTypeFilter } from "../components/SelectDateTypeFilter"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"

const useStyles = makeStyles()(() => ({
    infoWrapper: {
        padding: "1rem",
        width: "fit-content",
    },
    standardTableAction: {
        width: "fit-content",
        gap: "1rem",
        justifyContent: "end",
        flexWrap: "nowrap",
        padding: "1rem",
    },
}))

const selectAcquiringFees = (data: SupplierAcquiringFeesResponse) => data?.fees || []

export function AcquiringFees() {
    const { classes } = useStyles()

    const { supplierId } = useSupplier()
    const { l10n } = useAppLocalization()

    const maxDate = DateTime.now().minus({ days: 1 })
    const { startDate, endDate } = useInitialDateFilters(maxDate, maxDate)
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })

    const [dateType, setDateType] = useState<DateType>(DateType.BOOKING_DATE)
    const [filtersList, setFiltersList] = useState<{ merchantId?: string; adyentId?: string }>({
        merchantId: undefined,
        // adyentId: undefined,
    })

    const acquiringFees = useApiQuery(fetchSupplierAcquiringFees, {
        queryName: SupplierAcquiringReportsQuery.FEES_LIST,
        dependencies: {
            supplierId,
            fromDate: dateState.startDate.toISODate(),
            toDate: dateState.endDate.toISODate(),
            dateType,
        },
        select: selectAcquiringFees,
    })
    const tableData = useMemo(() => {
        return filterDataByMerchantAndAdyentId(acquiringFees.data, filtersList)
    }, [acquiringFees.data, filtersList])

    const { ApiQueryTable, columnFactory } = useApiQueryTable({ ...acquiringFees, data: tableData } as UseQueryResult<
        typeof tableData
    >)

    const columns = useMemo<MRT_ColumnDef<FeeItem>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-acquiring-fees-table-order-id-column"),
                accessorKey: "pspPaymentMerchantReference",
                muiTableHeadCellProps: {
                    align: "center",
                },
                muiTableBodyCellProps: {
                    align: "center",
                    style: {
                        verticalAlign: "baseline",
                    },
                },
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("supplier-acquiring-fees-table-psp-reference-column"),
                accessorKey: "pspPaymentPspReference",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("supplier-acquiring-reports-table-booked-time-column"),
                    accessorKey: "bookingDate",
                },
                (row) => row.bookingDate,
            ),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("supplier-acquiring-fees-table-available-date-column"),
                    accessorKey: "valueDate",
                },
                (row) => row.valueDate,
            ),
            columnFactory.typeColumn({
                header: l10n.getString("supplier-acquiring-fees-table-type-column"),
                accessorKey: "type",
                Cell: ({ row }) => (
                    <LocalizedEnum base="supplier-acquiring-fees-table-type-mode" enumValue={row.original.type}>
                        <>{row.original.type}</>
                    </LocalizedEnum>
                ),
            }),

            columnFactory.moneyColumn(
                {
                    header: l10n.getString("supplier-acquiring-reports-table-amount-column"),
                },
                (row) => row.amount,
                true,
            ),
            columnFactory.typeColumn({
                header: l10n.getString("supplier-acquiring-reports-table-merchant-column"),
                accessorKey: "merchant",
                Cell: ({ row }) => {
                    return (
                        <>
                            {row.original.merchant ? (
                                <>
                                    {row.original.merchant.caseNumber ? `${row.original.merchant.caseNumber}, ` : ""}
                                    {row.original.merchant.name}
                                </>
                            ) : null}
                        </>
                    )
                },
            }),
        ],
        [columnFactory, l10n],
    )

    const { handleExportRows } = useExportAcquiringReportsFeesToCsv(
        dateState.startDate?.toISODate() ?? "",
        dateState.endDate?.toISODate() ?? "",
    )

    return (
        <>
            <LocalizedStrict id="supplier-acquiring-fees-header" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPLIER_ADMIN_ACQUIRING_FEES} title="Acquiring fees">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-acquiring-reports">
                            <Breadcrumb>Reconciliation</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-acquiring-fees">
                            <Breadcrumb>Acquiring fees</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{
                        density: "spacious",
                        sorting: [
                            {
                                id: dateType === DateType.BOOKING_DATE ? "bookingDate" : "valueDate",
                                desc: false,
                            },
                        ],
                    }}
                    renderTopToolbar={({ table }) => (
                        <Box>
                            <Box className={classes.infoWrapper}>
                                <LocalizedStrict
                                    id="supplier-acquiring-fees-table-info-banner"
                                    attrs={{ description: true }}
                                >
                                    <InfoBox description="Data for this report becomes available on the next day, so you cannot see transfers for today or future transfers." />
                                </LocalizedStrict>
                            </Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <StandardTableFilter variant="left">
                                    <SelectDateTypeFilter dateType={dateType} setDateType={setDateType} />
                                    <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                                    <SelectMerchantAutocomplete
                                        merchantId={filtersList.merchantId}
                                        setMerchantId={(merchantId) =>
                                            setFiltersList((prev) => ({
                                                ...prev,
                                                merchantId,
                                            }))
                                        }
                                    />
                                    {/* <SelectAdyenAccountAutocomplete
                                adyentId={filtersList.adyentId}
                                setAdyentId={(adyentId) =>
                                    setFiltersList((prev) => ({
                                        ...prev,
                                        adyentId,
                                    }))
                                }
                            /> */}
                                </StandardTableFilter>
                                <Grid container className={classes.standardTableAction}>
                                    <Stack direction="row" spacing={1}>
                                        <StandardExportButton
                                            onClick={() => {
                                                handleExportRows(tableData)
                                            }}
                                        />
                                        <StandardToggleGlobalFilterButton table={table} />
                                        <MRT_FullScreenToggleButton table={table} />
                                    </Stack>
                                </Grid>
                            </Box>
                        </Box>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap>
                            <LocalizedStrict
                                id="supplier-acquiring-fees-table-number-of-transactions"
                                attrs={{ title: true }}
                            >
                                <TableNumberOfItemsBox
                                    data={table.getFilteredRowModel().rows}
                                    title="Number of transactions"
                                />
                            </LocalizedStrict>

                            <TableTotalAmountBox
                                data={table.getFilteredRowModel().rows}
                                accessor={(row) => row.original.amount}
                            />
                        </TableSummaryWrap>
                    )}
                />
            </Grid>
        </>
    )
}
